'use client'

import { useEffect, useMemo, useState } from 'react'

import { ChevronRight, Coins, History, Star } from 'lucide-react'

import { useContentHistory } from '../hooks/useContentHistory'
import {
  CONTENT_HISTORY_FILTER_TYPES,
  formatDateTime,
  isPassResourceType,
  resourceTypeBadgeVariant,
  resourceTypeLabel
} from '../lib/content-history-labels'
import type { ContentHistoryEntry } from '../types'

import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Card, CardBody } from '@/shared/ui/Card'
import { Input } from '@/shared/ui/input'
import { Pagination } from '@/shared/ui/Pagination'
import { SortDropdown } from '@/shared/ui/SortDropdown'

import { LevelDetailsModal } from './LevelDetailsModal'
import { MissionSubmissionsModal } from './MissionSubmissionsModal'
import { PassRewardDetailsModal } from './PassRewardDetailsModal'

const ALL_TYPES = 'all'

const TYPE_OPTIONS = [
  { value: ALL_TYPES, label: 'Todos os tipos' },
  ...CONTENT_HISTORY_FILTER_TYPES.map(({ value, label }) => ({ value, label }))
]

// A opção "Passe" cobre dois resource_type; as demais, um só.
const FILTER_TYPES_BY_VALUE = new Map(
  CONTENT_HISTORY_FILTER_TYPES.map(({ value, types }) => [value, types])
)

export function ContentHistorySection() {
  const [page, setPage] = useState(1)
  const [resourceType, setResourceType] = useState(ALL_TYPES)
  const [nameInput, setNameInput] = useState('')
  const [name, setName] = useState('')
  const [levelModal, setLevelModal] = useState<{
    id: number
    name: string | null
  } | null>(null)
  const [missionModal, setMissionModal] = useState<{
    id: number
    name: string | null
  } | null>(null)
  const [passModal, setPassModal] = useState<{
    id: number
    name: string | null
  } | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setName(nameInput.trim())
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [nameInput])

  const { records, pagination, isLoading, isError } = useContentHistory({
    page,
    resourceType: resourceType === ALL_TYPES ? undefined : FILTER_TYPES_BY_VALUE.get(resourceType),
    name: name || undefined
  })

  const pageCount = pagination?.page_count ?? 1

  const isLevel = (entry: ContentHistoryEntry) =>
    entry.resource_type === 'Level' && entry.resource_id != null

  const isEvidenceMission = (entry: ContentHistoryEntry) =>
    entry.resource_type === 'Mission' &&
    entry.resource_id != null &&
    entry.kind === 'evidence'

  // Resgate de passe abre o detalhe das recompensas entregues. Diferente de Nível
  // e Missão, o modal é buscado pelo id do próprio registro do histórico — é ele
  // que carrega o snapshot do que foi entregue.
  const isPassReward = (entry: ContentHistoryEntry) => isPassResourceType(entry.resource_type)

  const handleTypeChange = (value: string) => {
    setResourceType(value)
    setPage(1)
  }

  const content = useMemo(() => {
    if (isLoading) {
      return <p className="py-8 text-center text-sm text-muted-foreground">Carregando histórico…</p>
    }
    if (isError) {
      return (
        <p className="py-8 text-center text-sm text-destructive">
          Não foi possível carregar o histórico.
        </p>
      )
    }
    if (!records.length) {
      return (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <History className="size-8 text-muted-foreground" strokeWidth={1.25} aria-hidden />
          <p className="text-sm italic text-muted-foreground">
            Nenhuma conclusão encontrada para o filtro selecionado.
          </p>
        </div>
      )
    }

    return (
      <ul className="flex flex-col gap-1.5">
        {records.map(entry => {
          const level = isLevel(entry)
          const evidenceMission = isEvidenceMission(entry)
          const passReward = isPassReward(entry)
          const clickable = level || evidenceMission || passReward
          const handleClick = () => {
            if (level) {
              setLevelModal({ id: entry.resource_id as number, name: entry.resource_name })
            } else if (evidenceMission) {
              setMissionModal({ id: entry.resource_id as number, name: entry.resource_name })
            } else if (passReward) {
              setPassModal({ id: entry.id, name: entry.resource_name })
            }
          }
          const rowContent = (
            <>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <Badge variant={resourceTypeBadgeVariant(entry.resource_type)} className="shrink-0">
                  {resourceTypeLabel(entry.resource_type)}
                </Badge>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-sm font-medium">
                      {entry.resource_name ?? `#${entry.resource_id ?? '—'}`}
                    </p>
                    {entry.removed ? (
                      <Badge variant="light-red" className="shrink-0">
                        Removido
                      </Badge>
                    ) : null}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {formatDateTime(entry.created_at)}
                  </p>
                </div>
              </div>
              <div
                className={cn(
                  'flex shrink-0 items-center gap-2 text-sm sm:gap-3',
                  entry.removed && 'line-through opacity-70'
                )}
              >
                <span className="inline-flex items-center font-semibold text-primary">
                  <Star className="mr-1 size-3.5 shrink-0" aria-hidden />
                  {entry.point_reward}
                </span>
                <span className="inline-flex items-center font-semibold text-amber-600">
                  <Coins className="mr-1 size-3.5 shrink-0" aria-hidden />
                  {entry.coin_reward}
                </span>
                {clickable ? (
                  <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                ) : null}
              </div>
            </>
          )

          return (
            <li key={entry.id}>
              {clickable ? (
                <button
                  type="button"
                  onClick={handleClick}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors',
                    entry.removed
                      ? 'bg-red-500/10 hover:bg-red-500/20'
                      : 'bg-muted/40 hover:bg-muted/70'
                  )}
                >
                  {rowContent}
                </button>
              ) : (
                <div
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-3 py-2',
                    entry.removed ? 'bg-red-500/10' : 'bg-muted/40'
                  )}
                >
                  {rowContent}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    )
  }, [isLoading, isError, records])

  return (
    <Card variant="default">
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={nameInput}
            onChange={event => setNameInput(event.target.value)}
            placeholder="Buscar por nome"
            className="w-full sm:w-56"
          />
          <SortDropdown
            value={resourceType}
            options={TYPE_OPTIONS}
            onChange={handleTypeChange}
            label="Tipo"
          />
        </div>

        {content}

        {pageCount > 1 && (
          <Pagination
            currentPage={page}
            pageCount={pageCount}
            totalCount={pagination?.total_count ?? 0}
            onPrev={() => setPage(current => Math.max(1, current - 1))}
            onNext={() => setPage(current => Math.min(pageCount, current + 1))}
            ariaLabel="Paginação do histórico"
          />
        )}
      </CardBody>

      <LevelDetailsModal
        levelId={levelModal?.id ?? null}
        levelName={levelModal?.name ?? null}
        open={!!levelModal}
        onOpenChange={isOpen => {
          if (!isOpen) setLevelModal(null)
        }}
      />

      <MissionSubmissionsModal
        missionId={missionModal?.id ?? null}
        missionName={missionModal?.name ?? null}
        open={!!missionModal}
        onOpenChange={isOpen => {
          if (!isOpen) setMissionModal(null)
        }}
      />

      <PassRewardDetailsModal
        progressResourceId={passModal?.id ?? null}
        entryName={passModal?.name ?? null}
        open={!!passModal}
        onOpenChange={isOpen => {
          if (!isOpen) setPassModal(null)
        }}
      />
    </Card>
  )
}
